import { TITLE_TIER_LABELS, titleTierCountsFor } from '../../../lib/titleTierView.js'
import { useMemo } from 'react'
import { ownerName, UNASSIGNED_FILTER } from '../../../lib/championships.js'
import {coverageMemberOptions,schoolTeamOverlay} from '../../../lib/schoolTeamOverlay.js'
import styles from './pursuit.module.css'

/* ⚠️ A NAMED CONSTANT, NOT JSX TEXT. check-shipped-features.mjs greps the BUILT
 * bundle, and the JSX transform splits element text — a named constant survives
 * minification as one literal, so this control can never vanish from a deploy
 * without a gate refusing. Same reason InvisibleRecords.jsx and BodyCoverage.jsx
 * do it. */
export const UNASSIGNED_OPTION = 'No stored CRM rep'

/**
 * Filters built from the FULL universe of the SELECTED CATEGORY SPACE: sport,
 * industry, level, state, product category, incumbent, owner, year.
 *
 * Every option list is derived from `all`, which is the category-scoped
 * universe — so a Police space never offers a football sport and an RFP space
 * never offers a championship's level. A dimension with no values in the space
 * disables itself and says so instead of pretending to filter. That is why
 * PRODUCT CATEGORY (what an RFP is FOR — `sr_rfp_category`) and INDUSTRY (what
 * a corporation DOES — the employee-recognition qualifier) can both sit here
 * permanently: each is populated only in the spaces that record it, and inert
 * in the ones that don't.
 *
 * Two labels are read off the RECORDS rather than passed in, so the control
 * always names the dimension the way this space does: LEVEL reads "Pursuit
 * level" in the Corporate space (`hs_lead_status`), and PRODUCT reads whatever
 * the line calls its category.
 */
export default function FilterBar({ all, filters, setFilters, resultCount, nounPlural = 'championships', empty, searchNote = '', searchMode = 'all', individualOnly = false, onIndividualOnly, individualCount = 0, loading = false, actions = null, titleTiers = false, championshipBooks = false, supportBookAvailable = true, supportBookError = null, scopeSummary = null, scopeDetails = null }) {
  const tierCounts = useMemo(() => titleTierCountsFor(titleTiers ? all : []), [all, titleTiers])
  const coveragePeople = useMemo(() => coverageMemberOptions(all),[all])
  const unresolvedCoverage = all.filter(record => schoolTeamOverlay(record) && schoolTeamOverlay(record).status !== 'linked').length
  const opts = useMemo(() => {
    const uniq = (fn) => [...new Set(all.map(fn).filter((v) => v != null && v !== ''))].sort()
    const years = [...new Set(all.map((c) => (c.effDate ? c.effDate.getFullYear() : null)).filter(Boolean))].sort()
    return {
      sport: uniq((c) => c.sport),
      // WHAT THEY DO — the corporate qualifier. Blank everywhere else.
      industry: uniq((c) => c.industry),
      level: uniq((c) => c.level),
      state: uniq((c) => (c.state && c.state !== 'NATIONAL' ? c.state : null)),
      product: uniq((c) => c.product),
      incumbent: uniq((c) => c.incumbent),
      // Reps are being unassigned in HubSpot, so this list is legitimately
      // empty much of the time — build it from real names only, never a
      // placeholder, and let the control disable itself when there are none.
      owner: uniq(ownerName),
      year: years,
      /* ⚠️ THE COHORT THE OWNER FILTER COULD NOT NAME. Counted here off the
       * same `all` the options come from, so the number beside the control is
       * the number the control actually selects — not a figure from somewhere
       * else that can disagree with it. */
      unassigned: all.reduce((n, c) => (ownerName(c) ? n : n + 1), 0),
    }
  }, [all])

  // The space's own word for a dimension, taken from the first record that
  // names it. A championship record names neither, so both fall back to the
  // wording that has always shipped.
  const labelOf = (key, fallback) => all.find((c) => c[key])?.[key] || fallback
  const levelLabel = labelOf('levelLabel', 'Level')
  const industryLabel = labelOf('industryLabel', 'Industry')

  const set = (k) => (e) => setFilters((s) => ({ ...s, [k]: e.target.value }))
  // Cleared to the SAME shape the page owns, so a key can never be dropped from
  // the filter object by clearing it.
  const clear = () => setFilters(empty || { titleTier: '', sport: '', industry: '', level: '', state: '', product: '', incumbent: '', owner: '', year: '', q: '' })
  const anyActive = Object.values(filters).some(Boolean)

  return (
    <div className={`${styles.filterBar} cc-filterbar`}>
      <label className={styles.searchField}>Search all loaded {nounPlural}
        <input className={`input ${styles.search} cc-search`} type="search" placeholder="Championship, school, assigned rep, record ID…" value={filters.q} onChange={set('q')} />
      </label>
      <div className={styles.searchScope}>{loading ? 'Reading the complete space; matches are provisional.' : scopeSummary || `${all.length.toLocaleString()} records in this space. Every word is searched, in any order.`}</div>
      {/* ⚠️ A DIMENSION THIS SPACE DOES NOT HAVE IS ABSENT, NOT DISABLED.
        *
        * These used to render greyed with an "— n/a" label: Championships drew
        * "Industry — n/a" and "Product — n/a"; Greek drew Sport, Industry,
        * Level AND Product that way — four of nine controls inert, and they
        * still look like controls. A disabled dropdown communicates "you
        * can't filter by this *right now*", which is a different and wrong
        * claim: a Greek chapter has no sport at all.
        *
        * `Dim` renders nothing when the space has no values for a dimension —
        * unless a filter is somehow SET on it, in which case the control must
        * stay reachable so it can be cleared rather than becoming a hidden
        * filter nobody can turn off.
        *
        * The distinction below is deliberate and is why `owner`, `year` and
        * `incumbent` are NOT wrapped: those dimensions genuinely exist here and
        * their emptiness is a FACT worth stating ("all unassigned", "undated",
        * "unknown"). Absent means "not a thing here"; an empty-but-present
        * control means "a thing here that nothing has filled in yet." */}

      <div className={styles.filterTools}>
      <details className={styles.moreFilters}>
        <summary aria-label={titleTiers ? 'Filters, including award-level categories and counts' : undefined} title={titleTiers ? 'Award categories and counts, sport, state, assigned rep and more' : 'Sport, state, assigned rep and more'}>Filters{Object.entries(filters).filter(([key,value]) => key !== 'q' && value).length ? ` · ${Object.entries(filters).filter(([key,value]) => key !== 'q' && value).length} active` : ''}</summary>
        <div className={styles.moreFilterFields}>
      {scopeDetails}
      {titleTiers && <div className={`${styles.activeFilters} ${styles.titleTierFilters}`} aria-label="Award-level categories">
        <span>{loading ? 'Loaded award categories so far:' : 'Award categories in loaded records:'}</span>
        {Object.entries(TITLE_TIER_LABELS).map(([key, label]) => <button type="button" key={key} className={styles.lensChip}
          data-on={filters.titleTier === key ? '1' : '0'} aria-pressed={filters.titleTier === key} onClick={() => setFilters(current => ({ ...current, titleTier: current.titleTier === key ? '' : key }))}>
          {label} <b>{tierCounts[key].toLocaleString()}</b>
        </button>)}
      </div>}
      <Dim value={filters.sport} onChange={set('sport')} label="All sports" opts={opts.sport} />
      {/* WHAT THEY DO — `industry` in the Corporate space, absent elsewhere. */}


      <Dim value={filters.state} onChange={set('state')} label="All states" opts={opts.state} />
      {/* WHAT IT'S FOR — sr_rfp_category in the RFP space, absent elsewhere. */}

      {/* ⚠️ THE OWNER CONTROL NOW HAS A "NOBODY" OPTION, AND IT CARRIES ITS
        * COUNT. Brian: "If something is unassigned - i need to know about it.
        * we must be able to assign it." Until this option existed the answer
        * to the first half was "query the API" and the answer to the second
        * was "you cannot get the rows on screen to select them". The count
        * rides in the label because the whole point is that the number is
        * visible WITHOUT selecting anything — an option a manager has to click
        * to discover 2,399 records with no rep (measured 2026-10-13, off the
        * served snapshot, 15,814 records) is still a surface that hides
        * them. It is rendered only when there are some: an option reading
        * "Unassigned 0" is a claim, and a wrong one would be the reassuring
        * direction of wrong. */}
      {championshipBooks && <label>Championship assignments
        <select className="input" value={filters.book || ''} onChange={set('book')}>
          <option value="">All loaded championships</option>
          <option value="supported" disabled={!supportBookAvailable}>My recorded support assignments{!supportBookAvailable ? " — unavailable" : ""}</option>
        </select>
        {supportBookError && <span role="alert">{supportBookError} The supported-book filter is not applied; showing the loaded records.</span>}
      </label>}
      <Sel
        value={filters.owner}
        onChange={set('owner')}
        all={opts.owner.length ? (championshipBooks ? 'All CRM reps and bowl coverage' : 'All assigned reps') : 'No stored CRM reps'}
        opts={opts.owner}
        disabled={!opts.owner.length && !opts.unassigned && !coveragePeople.length}
        extra={[...(opts.unassigned
          ? [{ value: UNASSIGNED_FILTER, label: `${UNASSIGNED_OPTION} (${opts.unassigned.toLocaleString()})` }]
          : []),...coveragePeople.map(m=>({value:`school-team:${m.id}`,label:`${m.name} — bowl coverage (${m.role})`}))]}
      />
      {coveragePeople.filter(member => member.name === filters.owner).length > 1 && <p role="status">This rep name matches multiple system identities. Only the stored CRM name is filtered; choose the exact bowl coverage identity to include its team.</p>}
      {(String(filters.owner || '').startsWith('school-team:') || filters.book === 'supported') && unresolvedCoverage > 0 && <p role="status">{unresolvedCoverage} records have unresolved school-team coverage. They do not contribute confirmed coverage membership; clear the assignment filter to inspect their recorded scopes.</p>}



      {(coveragePeople.length > 0 || filters.bowlMember) && <label>Bowl coverage person
        <select className="input" value={filters.bowlMember || ''} onChange={set('bowlMember')}>
          <option value="">All linked bowl coverage members</option>
          {filters.bowlMember && !coveragePeople.some(m => String(m.id) === filters.bowlMember) && <option value={filters.bowlMember}>Selected member is not in the current linked coverage</option>}
          {coveragePeople.map(m => <option key={m.id} value={m.id}>{m.name} ({m.role})</option>)}
        </select>
        {filters.bowlMember && unresolvedCoverage > 0 && <span role="status">{unresolvedCoverage} records have missing, conflicting, review-required or unavailable coverage and are excluded from confirmed member matches. Clear this coverage filter to inspect them; this is not a zero-assignment claim.</span>}
      </label>}
      <Dim value={filters.industry} onChange={set('industry')} label="All industries" opts={opts.industry} />
      <Dim value={filters.level} onChange={set('level')} label={`All ${levelLabel.toLowerCase()}s`} opts={opts.level} />
      <Dim value={filters.product} onChange={set('product')} label="All products" opts={opts.product} />
      <Sel value={filters.year} onChange={set('year')} all={opts.year.length ? 'All years' : 'Year — undated'} opts={opts.year} disabled={!opts.year.length} />
      <Sel value={filters.incumbent} onChange={set('incumbent')} all={opts.incumbent.length ? 'All incumbents' : 'Incumbent — unknown'} opts={opts.incumbent} disabled={!opts.incumbent.length} />

        </div>
      </details>
      {actions}
      </div>
      {Object.entries(filters).some(([key,value]) => key !== 'q' && value) && <div className={styles.activeFilters} aria-label="Active filters">{Object.entries(filters).filter(([key,value]) => key !== 'q' && value).map(([key,value]) => <button key={key} className={styles.lensChip} onClick={() => setFilters(current => ({...current,[key]:''}))} aria-label={`Remove ${key} filter ${value}`}>{key === 'q' ? 'Search' : key === 'titleTier' ? 'Award category' : key}: {value === UNASSIGNED_FILTER ? UNASSIGNED_OPTION : key === 'titleTier' ? TITLE_TIER_LABELS[value] || value : value} ×</button>)}</div>}
      {anyActive && <button className={styles.clearBtn} onClick={clear}>Clear</button>}
      {/* ⚠️ ONE TAP TO THE INDIVIDUAL TITLES. An SEC tennis or track title
        * crowns a champion per event, each one a ring, and there is no way to
        * ask for "every individual sport" in an AND-only search box. */}
      {onIndividualOnly && individualCount > 0 && (
        <button
          type="button"
          className={styles.lensChip}
          data-on={individualOnly ? '1' : '0'}
          onClick={() => onIndividualOnly(!individualOnly)}
          title="Championships that crown a champion per event, weight class or division — each one a ring. Usually also a team title."
        >
          Individual titles <b>{individualCount.toLocaleString()}</b>
        </button>
      )}

      {/* ⚠️ "N shown" WHILE STILL PAGING IS A COUNT OF WHAT ARRIVED, NOT OF
        * WHAT EXISTS. Police mid-load read "6,800 shown" against a ~26k
        * universe with nothing marking it as in progress. The loading banner
        * above was honest; this number sat beside it looking final. */}
      <span className={`${styles.resultCount} cc-resultcount`} data-loading={loading ? '1' : '0'}>
        {resultCount.toLocaleString()} shown{loading ? ' so far — still loading' : ''}
      </span>

      {/* ⚠️ A ZERO THAT EXPLAINS ITSELF.
        *
        * Brian searched "baseball little league", got a bare 0, and reasonably
        * concluded the board was missing Little League. It wasn't — the old
        * matcher needed the words in a different order. A result count with no
        * account of itself is not a neutral fact, it is a claim about the DATA,
        * and it was wrong.
        *
        * So a partial or empty search now says which word excluded everything.
        * `partial` means we are showing the closest rather than nothing;
        * `none` means the board genuinely holds no such word, which is a real
        * finding rather than a failure. */}
      {searchNote && (
        <div className={searchMode === 'none' ? styles.searchNoteNone : styles.searchNote} role="status">
          {searchNote}
        </div>
      )}
    </div>
  )
}

/**
 * A dimension that only exists where the space actually records it.
 *
 * No options and no active value → renders NOTHING. No options but a value is
 * somehow set → renders enabled, so the filter can be cleared instead of
 * silently narrowing a board with no visible control to undo it.
 */
function Dim({ value, onChange, label, opts }) {
  if (!opts.length && !value) return null
  return <Sel value={value} onChange={onChange} all={label} opts={opts} disabled={false} />
}

/**
 * `extra` carries options whose VALUE is not their LABEL — today only the
 * unassigned sentinel. Kept as an explicit list rather than mixed into `opts`
 * so the derived-from-the-records options and the one synthetic option can
 * never be confused for each other: everything in `opts` is a value that
 * exists on a record, and everything in `extra` is a question about the
 * records. Rendered FIRST, because an ownerless record is the one a manager
 * has to act on.
 */
function Sel({ value, onChange, all, opts, disabled, extra = [] }) {
  return (
    <select aria-label={all} className={`input ${styles.filterSel} cc-filtersel`} value={value} onChange={onChange} disabled={disabled}>
      <option value="">{all}</option>
      {extra.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      {opts.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  )
}
